import { useState, useContext } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail, Heart } from "lucide-react";
import { useTranslation } from "react-i18next";
import { AppContext } from "@/App";
import { motion, AnimatePresence } from "framer-motion";
import LanguageSelector from "@/components/LanguageSelector";
import { useFavorites } from "@/contexts/FavoritesContext";

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { currency, setCurrency, areaUnit, setAreaUnit } = useContext(AppContext);
  const { t } = useTranslation();
  const { favoritesCount } = useFavorites();

  const navLinks = [
    { name: t("nav.home"), path: "/" },
    { name: t("nav.about"), path: "/nosotros" },
    { name: t("nav.properties"), path: "/comprar" },
    { name: t("nav.sell"), path: "/vender" },
    { name: "Blog", path: "/blog" },
    { name: t("nav.contact"), path: "/contacto" }
  ];


  const isActive = (path) => location.pathname === path;

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Top bar with contact info */}
      <div className="hidden md:block bg-[#050505] border-b border-white/5">
        <div className="container-custom py-2 flex justify-between items-center text-xs">
          <div className="flex items-center gap-6 text-[#A3A3A3]">
            <a href="tel:+50242547204" className="flex items-center gap-2 hover:text-[#D4AF37] transition-colors">
              <Phone size={12} />
              +502 4254-7204
            </a>
            <a href="mailto:hola@ikonicdeals.com" className="flex items-center gap-2 hover:text-[#D4AF37] transition-colors">
              <Mail size={12} />
              hola@ikonicdeals.com
            </a>
          </div>
          <div className="flex items-center gap-4">
            {/* Currency Toggle */}
            <div className="flex items-center gap-2 text-[#A3A3A3]">
              <button
                data-testid="currency-usd-btn"
                onClick={() => setCurrency("USD")}
                className={`px-2 py-1 text-xs transition-colors ${currency === "USD" ? "text-[#D4AF37]" : "hover:text-white"}`}>

                USD
              </button>
              <span>/</span>
              <button
                data-testid="currency-gtq-btn"
                onClick={() => setCurrency("GTQ")}
                className={`px-2 py-1 text-xs transition-colors ${currency === "GTQ" ? "text-[#D4AF37]" : "hover:text-white"}`}>

                GTQ
              </button>
            </div>
            {/* Area Toggle */}
            <div className="flex items-center gap-2 text-[#A3A3A3]">
              <button
                data-testid="area-m2-btn"
                onClick={() => setAreaUnit("m2")}
                className={`px-2 py-1 text-xs transition-colors ${areaUnit === "m2" ? "text-[#D4AF37]" : "hover:text-white"}`}>

                m²
              </button>
              <span>/</span>
              <button
                data-testid="area-varas-btn"
                onClick={() => setAreaUnit("varas2")}
                className={`px-2 py-1 text-xs transition-colors ${areaUnit === "varas2" ? "text-[#D4AF37]" : "hover:text-white"}`}>

                v²
              </button>
            </div>
            {/* Language Selector */}
            <LanguageSelector variant="ghost" />
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <nav className="glass">
        <div className="container-custom flex justify-between items-center py-3 sm:py-4 px-4 sm:px-6">
          {/* Brand Name */}
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <span className="text-[#D4AF37] font-semibold text-lg sm:text-xl tracking-wide">IKONIC DEALS</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8">
            {navLinks.map((link) =>
            <Link
              key={link.path}
              to={link.path}
              data-testid={`nav-${link.path.replace("/", "") || "home"}`}
              className={`nav-link text-xs xl:text-sm uppercase tracking-widest transition-colors ${
              isActive(link.path) ?
              "text-[#D4AF37]" :
              "text-white/70 hover:text-[#D4AF37]"}`
              }>

                {link.name}
              </Link>
            )}
            
            {/* Favorites Link */}
            <Link
              to="/favoritos"
              data-testid="nav-favorites"
              className={`relative flex items-center gap-1 text-xs xl:text-sm uppercase tracking-widest transition-colors ${
                isActive("/favoritos") ? "text-[#D4AF37]" : "text-white/70 hover:text-[#D4AF37]"
              }`}
            >
              <Heart size={16} className={favoritesCount > 0 ? "fill-red-500 text-red-500" : ""} />
              {favoritesCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                  {favoritesCount > 9 ? "9+" : favoritesCount}
                </span>
              )}
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-btn"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden text-white p-2">

            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen &&
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="mobile-menu fixed inset-0 top-16 z-40 md:hidden">

            <div className="container-custom py-8 flex flex-col gap-6">
              {navLinks.map((link, index) =>
            <motion.div
              key={link.path}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}>

                  <Link
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`block text-lg uppercase tracking-widest ${
                isActive(link.path) ? "text-[#D4AF37]" : "text-white/70"}`
                }>

                    {link.name}
                  </Link>
                </motion.div>
            )}
              
              {/* Mobile toggles */}
              <div className="pt-6 border-t border-white/10 flex flex-col gap-4">
                <div className="flex items-center gap-4">
                  <span className="text-sm text-[#A3A3A3]">Moneda:</span>
                  <button
                  onClick={() => setCurrency("USD")}
                  className={`px-3 py-1 text-sm ${currency === "USD" ? "bg-[#D4AF37] text-black" : "border border-white/20 text-white"}`}>

                    USD
                  </button>
                  <button
                  onClick={() => setCurrency("GTQ")}
                  className={`px-3 py-1 text-sm ${currency === "GTQ" ? "bg-[#D4AF37] text-black" : "border border-white/20 text-white"}`}>

                    GTQ
                  </button>
                </div>
                <div className="flex items-center gap-4">
                  <span className="text-sm text-[#A3A3A3]">Área:</span>
                  <button
                  onClick={() => setAreaUnit("m2")}
                  className={`px-3 py-1 text-sm ${areaUnit === "m2" ? "bg-[#D4AF37] text-black" : "border border-white/20 text-white"}`}>

                    m²
                  </button>
                  <button
                  onClick={() => setAreaUnit("varas2")}
                  className={`px-3 py-1 text-sm ${areaUnit === "varas2" ? "bg-[#D4AF37] text-black" : "border border-white/20 text-white"}`}>

                    v²
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        }
      </AnimatePresence>
    </header>);

};

export default Header;